class FittingboxPickerView extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        const originalButton = document.querySelector('.fittingbox-picker__button--viewer');

        if (!originalButton) return;

        this.addEventListener('click', () => {
            originalButton.click();
        });
    }
}

if (!customElements.get('fittingbox-picker-view')) {
    customElements.define('fittingbox-picker-view', FittingboxPickerView);
}

class FittingboxPicker extends HTMLElement {
    constructor() {
        super();

        // DOM references
        this.elements = {
            section: null,
            popup: null,
            popupTitle: null,
            container: null,
            vtoButton: null,
            viewerButton: null,

            // Color picker relocation (so it can be reverted)
            colorPicker: null,
            colorPickerMore: null,
            colorPickerMoreParent: null,
            colorPickerMoreNext: null,
        };

        // Parameters
        this.config = {
            frame: null,
            apiKey: null,
            scriptUrl: null,
            enableCustomCss: false,
        };

        // State
        this.instance = null;
        this.initialized = false;
        this.eventsBound = false;
        this.started = false;
        this.readyPromise = null;
        this.availabilityRequest = 0;
        this.vtoActive = false;
        this.popupClosedDuringLoad = false;
        this.colorPickerMoved = false;

        this.onVariantChanged = this.onVariantChanged.bind(this);
        this.onPopupClose = this.onPopupClose.bind(this);
    }

    async connectedCallback() {
        if (this.started) return;
        this.started = true;

        this.elements.section = this.closest('.fittingbox-picker');
        this.elements.popup = this.querySelector('.fittingbox-picker__popup');
        this.elements.popupTitle = this.elements.popup?.querySelector('.fittingbox-picker__title');
        this.elements.container = this.querySelector('.fittingbox-picker__container');
        this.elements.vtoButton = this.querySelector('.fittingbox-picker__button--vto');
        this.elements.viewerButton = this.querySelector('.fittingbox-picker__button--viewer');

        this.config.frame = this.dataset.fittingboxFrame;
        this.config.apiKey = this.dataset.fittingboxApiKey;
        this.config.scriptUrl = this.dataset.fittingboxScript;
        this.config.enableCustomCss = this.hasAttribute('data-fittingbox-enable-custom-css');

        if (!this.elements.container || !this.config.apiKey || !this.config.scriptUrl) {
            console.warn('[Fittingbox] Missing container or data attributes');
            return;
        }

        this.bindEvents();

        document.addEventListener('theme:product:variantChanged', this.onVariantChanged);
        document.addEventListener('theme:popup:close', this.onPopupClose);

        await this.updateAvailability();
    }

    moveColorPicker() {
        if (this.colorPickerMoved) return;

        const colorPicker = document.querySelector('product-content .color-picker');
        if (!colorPicker) return;

        const colorPickerMore = colorPicker.querySelector('.color-picker__more-wrapper');

        if (colorPickerMore) {
            // Remember where it came from so it can be put back
            this.elements.colorPickerMore = colorPickerMore;
            this.elements.colorPickerMoreParent = colorPickerMore.parentNode;
            this.elements.colorPickerMoreNext = colorPickerMore.nextSibling;
            this.append(colorPickerMore);
        }

        colorPicker.classList.add('hidden');

        this.elements.colorPicker = colorPicker;
        this.colorPickerMoved = true;
    }

    restoreColorPicker() {
        if (!this.colorPickerMoved) return;

        const { colorPicker, colorPickerMore, colorPickerMoreParent: parent, colorPickerMoreNext } = this.elements;

        if (colorPickerMore && parent?.isConnected) {
            // Put it back in its original position (falls back to the end of the parent)
            const next = colorPickerMoreNext?.parentNode === parent ? colorPickerMoreNext : null;
            parent.insertBefore(colorPickerMore, next);
        }

        colorPicker?.classList.remove('hidden');

        this.elements.colorPicker = null;
        this.elements.colorPickerMore = null;
        this.elements.colorPickerMoreParent = null;
        this.elements.colorPickerMoreNext = null;
        this.colorPickerMoved = false;
    }

    // ---------------------------------------------------------------
    // Variant change
    // ---------------------------------------------------------------

    async onVariantChanged(event) {
        // Adjust this path if your event payload is shaped differently
        const barcode = event.detail?.variant?.barcode;
        const frame = barcode ? String(barcode).trim() : '';

        if (frame === this.config.frame) return;

        this.config.frame = frame;
        this.dataset.fittingboxFrame = frame;

        await this.updateAvailability();
        this.syncInstance();
    }

    // ---------------------------------------------------------------
    // Popup close
    // ---------------------------------------------------------------

    onPopupClose(event) {
        // Only react to the Fittingbox popup (ignore cart drawer, search, etc.)
        const popup = event.detail?.popup || event.target;
        const popupId = popup?.id || event.detail?.id;

        if (popupId !== 'productPopup--fittingbox') return;

        this.closePopupSession();
    }

    closePopupSession() {
        // Prevents startVto()/startViewer() if the popup closed while the script was still loading
        this.popupClosedDuringLoad = true;

        this.stop();
        this.hideContainer();
    }

    // ---------------------------------------------------------------
    // Availability / visibility
    // ---------------------------------------------------------------

    async updateAvailability() {
        const requestId = ++this.availabilityRequest;

        const item = this.config.frame ? await this.checkAvailability() : null;

        // A newer variant change happened while we were waiting
        if (requestId !== this.availabilityRequest) return;

        const hasVto = Boolean(item?.available);
        const hasViewer = Boolean(item?.available && item?.viewerCompatible);

        this.elements.vtoButton?.classList.toggle('hidden', !hasVto);

        const galleryViewerButton = document.querySelector('fittingbox-picker-view');
        galleryViewerButton?.classList.toggle('hidden', !hasViewer);

        this.elements.section?.classList.toggle('hidden', !hasVto && !hasViewer);

        if (hasVto || hasViewer) {
            this.moveColorPicker();
        } else {
            this.restoreColorPicker();
            this.stop();
            this.hideContainer();
        }
    }

    async checkAvailability() {
        const { apiKey, frame } = this.config;
        const url = `https://product-api.fittingbox.com/glasses-metadata/availability?apiKey=${encodeURIComponent(apiKey)}&uidList=${encodeURIComponent(frame)}`;

        try {
            const response = await fetch(url);
            if (!response.ok) return null;

            const data = await response.json();
            return data.find((entry) => entry.uid === frame) || null;
        } catch (error) {
            console.error('[Fittingbox] Availability error:', error);
            return null;
        }
    }

    // Keep an already-created widget in sync with the new frame
    syncInstance() {
        if (!this.instance || !this.initialized) return;

        if (this.config.frame && typeof this.instance.setFrame === 'function') {
            this.instance.setFrame(this.config.frame);
            return;
        }

        // Fallback: tear down, it will be re-created on the next click
        this.stop();
        this.hideContainer();
        this.elements.container.innerHTML = '';
        this.instance = null;
        this.initialized = false;
        this.readyPromise = null;
    }

    // ---------------------------------------------------------------
    // Click handling
    // ---------------------------------------------------------------

    bindEvents() {
        if (this.eventsBound) return;
        this.eventsBound = true;

        const { vtoButton, viewerButton } = this.elements;

        vtoButton?.addEventListener('click', () => this.handleClick(vtoButton, 'vto'));
        viewerButton?.addEventListener('click', () => this.handleClick(viewerButton, 'viewer'));
    }

    async handleClick(button, mode) {
        this.popupClosedDuringLoad = false;

        if (this.elements.popupTitle) {
            this.elements.popupTitle.innerText = button.innerText.trim();
        }

        try {
            await this.ensureReady();
        } catch (error) {
            console.error('[Fittingbox] Could not start:', error);
            return;
        }

        // The popup was closed while the script / widget was loading
        if (this.popupClosedDuringLoad) return;

        if (mode === 'vto') this.startVto();
        else this.startViewer();
    }

    // ---------------------------------------------------------------
    // Fittingbox loading
    // ---------------------------------------------------------------

    ensureReady() {
        if (!this.readyPromise) {
            this.readyPromise = this.loadScript()
                .then(() => this.init())
                .catch((error) => {
                    this.readyPromise = null; // allow retry on next click
                    throw error;
                });
        }

        return this.readyPromise;
    }

    loadScript() {
        if (typeof FitMix !== 'undefined') {
            return Promise.resolve();
        }

        if (!FittingboxPicker.scriptPromise) {
            const { scriptUrl } = this.config;

            FittingboxPicker.scriptPromise = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = scriptUrl;
                script.async = true;
                script.onload = resolve;
                script.onerror = () => {
                    FittingboxPicker.scriptPromise = null;
                    reject(new Error(`Failed to load ${scriptUrl}`));
                };
                document.head.appendChild(script);
            });
        }

        return FittingboxPicker.scriptPromise;
    }

    init() {
        return new Promise((resolve, reject) => {
            if (typeof FitMix === 'undefined') {
                reject(new Error('FitMix is not available'));
                return;
            }

            const customCss = `
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap');

                html,
                :host {
                    --default-font-family: 'Inter', sans-serif;
                    --linkColor: #3a77e1;
                    --linkHover: #6dbbec;
                }

                /* Custom styling for the Fittingbox Try-On interface */
                [data-style^="vto-adv"] {
                    font-family: inherit;
                }

                #vtoAdvDisclaimer {

                    & > .size-full {

                        & > .sticky {
                            box-shadow: none;
                            padding: 22.5px 30px !important;
                            border-block-start: 1px solid rgba(0, 0, 0, .05);
                        }
                    }
                }

                #vtoAdvDisclaimer > .size-full > .mx-auto {
                    max-width: none;
                    margin-block: auto;
                    padding: 30px;
                    font-size: 14px;

                    & > vto-advanced-disclaimer-explanation {

                        & > h1 {
                            font-size: 24px;
                            text-wrap: balance;
                            text-align: center;
                        }

                        & > [data-style="vto-adv-disclaimer-explanation"] {
                            gap: 24px;

                            & > .gap-6 {
                                gap: 12px;
                            }
                        }
                    }

                    [data-style="vto-adv-disclaimer-terms-policy"] {
                        text-wrap: balance;
                        text-align: center;
                    }

                    vto-advanced-disclaimer-terms-policy {

                        [role="button"] {
                            color: var(--linkColor);
                            text-decoration: none;
                            font-weight: inherit;

                            &:hover {
                                color: var(--linkHover);
                            }
                        }
                    }
                }

                [data-style="vto-adv-disclaimer-actions"] {
                    --buttonRadius: 5px;
                    --buttonPadding: 14px;
                    --buyButtonBgColor: #000000;
                    --buyButtonBorderColor: #000000;
                    --buyButtonBorderHover: #000000;
                    --buyButtonColor: #ffffff;
                    --buttonBorderWidth: 1px;
                    --buttonBorderStyle: solid;
                    --buttonFontSize: 14px;
                    // flex-direction: column-reverse;
                    gap: 16px;
                }

                #vtoAdvDisclaimerBtnAgree {
                    --buttonBgColor: var(--buyButtonBgColor);
                    --buttonColor: var(--buyButtonColor);
                    --buttonBorderColor: var(--buyButtonBorderColor);
                    --buttonBorderHover: var(--buyButtonBorderHover);
                }

                #vtoAdvDisclaimerBtnDisagree {
                    --buttonBgColor: var(--buyButtonColor);
                    --buttonColor: var(--buyButtonBgColor);
                    --buttonBorderColor: var(--buyButtonBgColor);
                }

                #vtoAdvDisclaimerBtnAgree,
                #vtoAdvDisclaimerBtnDisagree {
                    display: inline-block;
                    padding: var(--buttonPadding);
                    color: var(--buttonColor);
                    font-size: var(--buttonFontSize);
                    letter-spacing: .56px;
                    line-height: 1.15;
                    font-weight: 500;
                    font-style: inherit;
                    text-align: center;
                    text-transform: uppercase;
                    text-decoration: none;
                    background: var(--buttonBgColor);
                    border-radius: var(--buttonRadius);
                    border: var(--buttonBorderWidth) var(--buttonBorderStyle) var(--buttonBorderColor);
                    cursor: pointer;
                    box-sizing: border-box;
                    transition: all .2s ease-in-out;
                    width: 100%;

                    &:hover {
                        border-color: var(--buttonBorderHover);
                        color: var(--buttonBgColor);
                        background: var(--buttonColor);
                    }
                }
            `;

            const params = {
                apiKey: this.config.apiKey,
                frame: this.config.frame, // always the current variant's barcode

                // Only include custom CSS in demo mode
                ...(this.config.enableCustomCss && { css: customCss }),

                onStopVto: () => {
                    this.vtoActive = false;
                    this.hideContainer();
                },
                onIssue: (issue) => console.error('[Fittingbox] Issue:', issue),
                onMode: (mode) => console.log('[Fittingbox] Mode:', mode),
            };

            this.instance = FitMix.createWidget(this.elements.container.id, params, () => {
                this.initialized = true;

                // The variant may have changed while the widget was loading
                if (this.config.frame && this.config.frame !== params.frame) {
                    this.instance.setFrame(this.config.frame);
                }

                resolve();
            });
        });
    }

    // ---------------------------------------------------------------
    // Start / stop
    // ---------------------------------------------------------------

    startVto() {
        if (!this.initialized) return;
        this.showContainer();
        this.vtoActive = true;
        this.instance.startVto('live');
    }

    startViewer() {
        if (!this.initialized) return;
        this.showContainer();
        this.instance.setMode('viewer');
    }

    showContainer() {
        this.elements.container.style.display = 'block';
    }

    hideContainer() {
        this.elements.container.style.display = 'none';
    }

    stop() {
        if (this.vtoActive) {
            this.vtoActive = false;
            this.instance?.stopVto();
        }
    }

    disconnectedCallback() {
        document.removeEventListener('theme:product:variantChanged', this.onVariantChanged);
        document.removeEventListener('theme:popup:close', this.onPopupClose);

        this.availabilityRequest++;
        this.restoreColorPicker();
        this.stop();
        this.instance = null;
        this.initialized = false;
        this.readyPromise = null;
        this.eventsBound = false;
        this.started = false;
    }
}

if (!customElements.get('fittingbox-picker')) {
    customElements.define('fittingbox-picker', FittingboxPicker);
}